import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { API_URL } from "../constants";
import { formatDureeDepuis } from "./motorHelpers";

export function useMotorsList({ userId, token, offset, limit }) {
  const [motorsList, setMotorsList] = useState([]);
  const [totalMotors, setTotalMotors] = useState(0);
  const [clientLocMap, setClientLocMap] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [motorVibrationData, setMotorVibrationData] = useState({});
  const [dureeFctMap, setDureeFctMap] = useState({});
  const [consoMap, setConsoMap] = useState({});

  // ─── Fetch liste moteurs + clientLocs en parallèle ───────────────────────
  const fetchMotors = useCallback(async () => {
    try {
      const [motorsRes, clientLocRes] = await Promise.all([
        axios.get(
          `${API_URL}/moteur/listmoteur?id=${userId}&offset=${offset}&limit=${limit}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(`${API_URL}/moteur/listclientloc`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const map = {};
      clientLocRes.data.data.forEach((loc) => {
        map[loc._id] = { _id: loc._id, client: loc.client, localisation: loc.localisation };
      });
      setClientLocMap(map);

      const motors = motorsRes.data.data.map((motor) => {
        const loc = map[motor.clientLoc];
        return {
          ...motor,
          client: loc?.client ?? "Pas de client",
          localisation: loc?.localisation ?? "Pas de localisation",
        };
      });
      setMotorsList(motors);
    } catch (err) {
      console.error("Erreur fetchMotors :", err.message);
    }
  }, [userId, offset, limit, token]);

  // ─── Fetch count total ────────────────────────────────────────────────────
  const fetchTotal = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/moteur/countmoteurs?id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotalMotors(res.data.count);
    } catch (err) {
      console.error("Erreur fetchTotal :", err.message);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchMotors();
    fetchTotal();
  }, [fetchMotors, fetchTotal]);

  // ─── Vérification vibratoire en parallèle (Promise.all) ──────────────────
  const checkMotorsData = useCallback(async (motors) => {
    const startDate = moment().format("YYYY-MM-DD");
    const endDate = moment().add(1, "day").format("YYYY-MM-DD");

    const results = await Promise.allSettled(
      motors.map(async (motor) => {
        const idForRequest =
          motor.id_moteurs_replace?.length > 0
            ? motor.id_moteurs_replace[motor.id_moteurs_replace.length - 1]
            : motor._id;

        const res = await axios.get(
          `${API_URL}/?d1=${startDate}&d2=${endDate}&id=${idForRequest}&granularity=minute`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        return { motor, data: res.data };
      })
    );

    const newErrors = {};
    const newVibration = {};

    results.forEach((result) => {
      if (result.status === "rejected") return;
      const { motor, data } = result.value;

      if (data.data.length === 0) {
        newErrors[motor._id] = "Le capteur n'a renvoyé aucune donnée aujourd'hui.";
        return;
      }

      const lastTrame = data.data[data.data.length - 1];
      newVibration[motor._id] = { ampliX1: lastTrame.ampliX1 };

      const diff = moment.duration(moment().diff(moment(lastTrame.timestamp)));
      if (diff.asHours() >= 1) {
        newErrors[motor._id] = `Le capteur ne renvoie plus de données depuis ${formatDureeDepuis(lastTrame.timestamp)}`;
      }
    });

    setErrorMessages(newErrors);
    setMotorVibrationData(newVibration);
  }, [token]);

  useEffect(() => {
    if (motorsList.length > 0) checkMotorsData(motorsList);
  }, [motorsList, checkMotorsData]);

  // ─── Fetch durées + consommations en parallèle (Promise.all) ─────────────
  useEffect(() => {
    if (motorsList.length === 0) return;

    let isMounted = true;

    const fetchDurees = async () => {
      const entries = await Promise.allSettled(
        motorsList.map(async (motor) => {
          let totalDuree = motor.dureeFct || 0;
          let totalConso = motor.consommation || 0;

          if (motor.id_moteurs_replace?.length > 0) {
            const res = await axios.get(
              `${API_URL}/moteur/${motor._id}/remplacement`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.status === 200 && res.data?.remplacements) {
              res.data.remplacements.forEach((r) => {
                if (r.dureeFct) totalDuree += r.dureeFct;
                if (r.consommation) totalConso += r.consommation;
              });
            }
          }

          return { id: motor._id, totalDuree, totalConso };
        })
      );

      if (!isMounted) return;

      const newDuree = {};
      const newConso = {};
      entries.forEach((entry) => {
        if (entry.status === "fulfilled") {
          const { id, totalDuree, totalConso } = entry.value;
          newDuree[id] = totalDuree;
          newConso[id] = totalConso;
        }
      });

      setDureeFctMap(newDuree);
      setConsoMap(newConso);
    };

    fetchDurees();
    return () => { isMounted = false; };
  }, [motorsList, token]);

  return {
    motorsList,
    totalMotors,
    clientLocMap,
    errorMessages,
    motorVibrationData,
    dureeFctMap,
    consoMap,
    refetch: fetchMotors,
  };
}